/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

import { isUndefined } from 'underscore';
import * as StateNames from 'state-names';
import './subheader.component.less';

import { STORE_TOKEN } from 'ng/root-store/root-store.tokens';
import * as SystemInfoSelectors from 'ng/root-store/system-info/system-info.selectors';
import { getParentStateName } from 'ng/modules/core/utils/router-states.utils';

import {
    STATE_ACCESS_SERVICE_TOKEN,
} from 'ng/modules/core/services/state-access/state-access.service';

/**
 * @ngdoc component
 * @name  subheader
 * @param {Item?} - Item might be passed to figure out which tabs to show or hide.
 * @description
 *
 *     Subheader component for the subheader navbar below the main application navbar.
 */
//TODO rename
class SubheaderController {
    constructor(
        $state,
        $transitions,
        $stateParams,
        store,
        infraCloudState,
        appStateTree,
        stateManagerService,
        stateAccessService,
    ) {
        this.$state = $state;
        this.$transitions_ = $transitions;
        this.$stateParams = $stateParams;
        this.store = store;
        this._infraCloudState = infraCloudState;
        this.appStateTree = appStateTree;
        this.stateManagerService = stateManagerService;
        this.stateAccessService = stateAccessService;

        /**
         * Set of parent states for which metric controls are not shown in the main header.
         */
        this.parentStatesWithoutMetricControl = new Set([
            StateNames.VS_VS_DETAIL,
            StateNames.SERVICE_ENGINE_SERVICE_ENGINE_DETAIL,
            StateNames.POOL_POOL_DETAIL,
            StateNames.GSLBSERVICE_GSLBSERVICE_DETAIL,
            StateNames.VS_DETAIL_SECURITY,
        ]);
    }

    $onInit() {
        this.setProperties_();
        this.showTimeframe = this.getTimeframeVisibility_();
        this.showMetricSelector = this.getMetricSelectorVisibility_();

        this.destroyTransitionListener = this.$transitions_.onSuccess({}, () => {
            if (this.parent_.name !== this.getParent_().name) {
                this.setProperties_();
            }

            this.showTimeframe = this.getTimeframeVisibility_();
            this.showMetricSelector = this.getMetricSelectorVisibility_();
        });

        this.localSiteIsGslbLeader = false;

        this.localSiteIsGslbLeaderSubscription = this.store.select(
            SystemInfoSelectors.selectLocalSiteIsGslbLeader,
        ).subscribe(isLeader => this.localSiteIsGslbLeader = isLeader);

        this.hasGslbConfig = false;

        this.hasGslbConfigSubscription = this.store.select(SystemInfoSelectors.selectHasGslbConfig)
            .subscribe(hasGslb => this.hasGslbConfig = hasGslb);
    }

    /**
     * Returns true if the header should show the Metric selector dropdown (display value type).
     * @return {boolean}
     * @protected
     */
    getMetricSelectorVisibility_() {
        return this.$state.current.data?.tfGroup !== 'logs' || false;
    }

    /**
     * Sets properties used by the template along with the parent state name. States get reset
     * only if the parent name changes.
     */
    setProperties_() {
        this.parent_ = this.getParent_();
        this.states = this.getStates_();
    }

    /**
     * Returns the parent state.
     * @return {Object}
     */
    getParent_() {
        return this.$state.get(getParentStateName(this.$state.current.name));
    }

    /**
     * Returns true if the timeframe should be shown in the subheader.
     * @return {Boolean}
     */
    getTimeframeVisibility_() {
        return this.$state.current.data?.showTimeframe || this.parent_?.data?.showTimeframe;
    }

    /**
     * Returns true if tab should be hidden due to special circumstances (ex. only show if GSLB
     * config exists), unrelated to permissions.
     * @param  {Object} state - Name of the state defined in state tree.
     * @return {Boolean}
     */
    //TODO use some custom state property to put checks in along with
    // resolvable Item shared by all the nested states
    isHidden(state) {
        const { name: stateName } = state;

        switch (stateName) {
            case 'authenticated.profile.profiles.gslb-healthmonitor-list':
                return !this.haveGSLBConfig();

            case StateNames.GSLB_GEO_PROFILE:
                return !this.localSiteIsGslbLeader;

            case StateNames.VS_DETAIL_DNS_RECORDS:
                return !this.item || !this.item.isDNS();

            default:
                return !this.stateAccessService.isAccessible(stateName);
        }
    }

    /**
     * Sets the array of states to be shown in the template.
     * For App level header, it's the parent level objects.
     */
    getStates_() {
        if (this.isAppLevel) {
            return this.appStateTree;
        } else {
            return this.parent_.children.filter(child => !isUndefined(child.title));
        }
    }

    /**
     * Returns true if metric value and timeframe selectors should be shown in the subheader.
     * @return {boolean}
     */
    isMetricControlsShown() {
        const { current: { data } } = this.$state;
        const { parentState } = data;

        return this.showTimeframe &&
            !(this.isAppLevel && this.parentStatesWithoutMetricControl.has(parentState));
    }

    /**
     * Return the permission associated with a state.
     * @param {string} stateName - Name of the state to retrieve the permission for.
     * @returns string
     */
    getPermissionFromStateName(stateName) {
        return this.stateAccessService.getPermissionFromStateName(stateName);
    }

    $onDestroy() {
        this.destroyTransitionListener();
        this.localSiteIsGslbLeaderSubscription.unsubscribe();
        this.hasGslbConfigSubscription.unsubscribe();
    }
}

SubheaderController.$inject = [
    '$state',
    '$transitions',
    '$stateParams',
    STORE_TOKEN,
    'infraCloudState',
    'appStateTree',
    'stateManagerService',
    STATE_ACCESS_SERVICE_TOKEN,
];

angular.module('aviApp').component('subheader', {
    controller: SubheaderController,
    templateUrl: 'src/components/common/subheader/subheader.html',
    bindings: {
        item: '<?',
        isAppLevel: '<?',
    },
});
